import {
  paginationWithSelect,
  request,
} from '@fastlessjs/backoffice-layer/dist/hooks/stores/helpers';
import { APIResponse } from '@fastlessjs/backoffice-layer/dist/hooks/stores/types';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { SuiviProduct } from 'products/types';

import { APIOptions } from '../../types';
import { formatDate, stripLeadingZerosForApi } from '../../utils';
import { Traces, TraceReturns, TraceReturn, Trace } from '../types';
import { resolveProcessingStatus } from '../utils/traceStatusDisplay';

debug.registerComponent('trace-hooks');

const basePath = '/returns/suivi';

type PageParam = { next?: string; pageSize?: number } | undefined;

// The cursor returned by the API is standard base64 and can contain `+`, which
// a raw query string would turn back into a space server-side.
const paginationQuery = (pageParam: PageParam) => {
  const qs = new URLSearchParams();
  if (pageParam?.next) qs.set('next', pageParam.next);
  if (pageParam?.pageSize) qs.set('pageSize', String(pageParam.pageSize));
  const query = qs.toString();
  return query ? `?${query}` : '';
};

const getSuivis = async (pageParam: PageParam, options: APIOptions) => {
  const { tenantId, endpoint } = options;

  return request(`${endpoint || basePath}/${paginationQuery(pageParam)}`, {
    method: 'get',
    options: {
      headers: {
        'x-tenant-id': tenantId,
      },
    },
  });
};

const getSuivi = async (options: APIOptions) => {
  const { tenantId } = options;

  return request(`${basePath}/${tenantId}`, {
    method: 'get',
  });
};

const getSuiviOrder = async (options: APIOptions) => {
  const { tenantId } = options;
  const clientId = stripLeadingZerosForApi(options.clientId);
  const orderId = stripLeadingZerosForApi(options.orderId);
  // The first path segment is the Eureka entity, not the organization: it is
  // forwarded verbatim into <entp> on the WWSSTATERest call. It is always EPI.
  return request(`/orders/EPI/${clientId}/order/${orderId}`, {
    method: 'get',
    disableNotif: true,
    options: {
      headers: {
        'x-tenant-id': tenantId,
      },
    },
  });
};

const getSuiviProducts = async (pageParam: PageParam, options: APIOptions) => {
  const { tenantId, suiviId } = options;

  return request(`${basePath}/${suiviId}/products${paginationQuery(pageParam)}`, {
    method: 'get',
    options: {
      headers: {
        'x-tenant-id': tenantId,
      },
    },
  });
};

const resolveSuiviIdFromRow = (
  row: Partial<Trace> & Record<string, unknown>,
): string | undefined => {
  if (row.suiviId) {
    return String(row.suiviId);
  }
  const nsui = row.nsui ?? row.NSUI ?? row.eurekaNsui;
  return nsui != null && nsui !== '' ? String(nsui) : undefined;
};

const getReturnsByOrder = async (options: APIOptions) => {
  const clientId = stripLeadingZerosForApi(options.clientId);
  const orderId = stripLeadingZerosForApi(options.orderId);

  return request(`/returns/${clientId}/order/${orderId}`, {
    method: 'get',
    disableNotif: true,
    options: {
      headers: {
        'x-tenant-id': options.tenantId ?? clientId,
      },
    },
  });
};

const useSuivi = (options?: APIOptions) => {
  return useInfiniteQuery({
    queryKey: ['suivis', options?.tenantId],
    queryFn: ({ pageParam }) => getSuivis(pageParam as PageParam, options || {}),
    enabled: !!options?.tenantId,
    initialPageParam: undefined,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages || [];
      const hasPending = pages.some((page: APIResponse<Traces>) =>
        (page?.data || []).some((entry) => entry?.returnStatus === 'pending'),
      );
      return hasPending ? 5000 : false;
    },
    ...paginationWithSelect<Traces>(),
  });
};

const useSuiviProducts = (options?: APIOptions) => {
  return useInfiniteQuery({
    queryKey: ['suivis', 'products', options?.suiviId, options?.tenantId],
    queryFn: ({ pageParam }) => getSuiviProducts(pageParam as PageParam, options || {}),
    enabled: !!options?.tenantId && !!options?.suiviId,
    initialPageParam: undefined,
    ...paginationWithSelect<SuiviProduct[]>(),
  });
};

const useSuiviReturn = (options: APIOptions) => {
  debug.trackAccess('useSuiviReturn', 'hook execution');
  debug.log('useSuiviReturn', `Fetching data for tenantId: ${options?.tenantId}`);

  return useQuery({
    queryKey: ['suivi', options?.tenantId],
    queryFn: () => getSuivi(options),
    select: (response: APIResponse<TraceReturns>) => {
      const row = response.data?.[0];
      if (!row) {
        return undefined;
      }

      const nsuiValue = row.nsui || (row.eurekaNsui ? String(row.eurekaNsui) : undefined);

      return {
        ...row,
        nsui: nsuiValue ? String(nsuiValue) : '',
        dlit: row.dlit ? formatDate(String(row.dlit)) : row.dlit,
      };
    },
    enabled: !!options?.tenantId,
    refetchInterval: (query) => {
      const data = query.state.data as TraceReturn | undefined;
      return data?.returnStatus === 'pending' ? 5000 : false;
    },
  });
};

const useSuiviOrder = (options: APIOptions) => {
  return useQuery({
    queryKey: ['suivi-order', options?.tenantId, options?.orderId, options?.clientId],
    queryFn: () => getSuiviOrder(options),
    select: (response: APIResponse<{ suiviId: string }[]>) => ({
      ...response.data?.[0],
      suiviId: response.data[0]?.suiviId && String(response.data[0].suiviId),
    }),
    enabled: !!options?.tenantId && !!options?.orderId && !!options?.clientId,
    retry: false,
  });
};

export type ReturnsByOrderResult = {
  rows: Trace[];
  rowCount: number;
};

const normalizeReturnSearchRow = (
  row: Partial<Trace> & Record<string, unknown>,
): Trace | undefined => {
  const suiviId = resolveSuiviIdFromRow(row);
  if (!suiviId) {
    return undefined;
  }

  return {
    ...row,
    suiviId,
    orderId: String(row.orderId ?? row.cal0006 ?? ''),
    // eslint-disable-next-line no-underscore-dangle -- DynamoDB audit field on Trace
    _createdAt: String(row._createdAt ?? row.dlit ?? ''),
    clientUserEmail: String(row.clientUserEmail ?? ''),
    clientUserName: String(row.clientUserName ?? ''),
    displayReturnStatus: row.returnStatus,
    displayProcessingStatus: resolveProcessingStatus(row),
  } as Trace;
};

const useReturnsByOrder = (options: APIOptions) => {
  debug.trackAccess('useReturnsByOrder', 'hook execution');

  return useQuery({
    queryKey: ['returns-by-order', options?.clientId, options?.orderId],
    queryFn: () => getReturnsByOrder(options),
    select: (response: APIResponse<Trace[]>) => {
      const seen = new Set<string>();
      const rows = (response.data ?? [])
        .map((row) =>
          normalizeReturnSearchRow(row as Partial<Trace> & Record<string, unknown>),
        )
        .filter((row): row is Trace => {
          if (!row) {
            return false;
          }
          if (seen.has(row.suiviId)) {
            return false;
          }
          seen.add(row.suiviId);
          return true;
        });

      if (rows.length === 0) {
        return undefined;
      }

      return { rows, rowCount: rows.length } satisfies ReturnsByOrderResult;
    },
    enabled: Boolean(options?.orderId && options?.clientId),
    retry: false,
  });
};

export { useReturnsByOrder, useSuivi, useSuiviOrder, useSuiviProducts, useSuiviReturn };

debug.markInitialized('trace-hooks');
